import { acceptHMRUpdate, defineStore } from 'pinia'
import { axiosInstance } from '@/api/api'

export type NotificationLevel = 'info' | 'success' | 'error'

export type NotificationEvent =
  | 'admin_access_approved'
  | 'admin_access_rejected'
  | 'join_request_received'
  | 'join_request_approved'
  | 'join_request_rejected'

export interface NotificationRow {
  id: number
  event: NotificationEvent
  level: NotificationLevel
  title: string
  message: string
  data: Record<string, string>
  is_read: boolean
  created_at: string
}

// Poll the unread count while the user is logged in so the bell badge stays
// reasonably fresh without needing a full page reload (events are admin-driven
// and not time-critical, so a minute is plenty). Kept module-level so it is a
// singleton across the persistent navbar instances.
const POLL_INTERVAL_MS = 60_000
let pollTimer: ReturnType<typeof setInterval> | null = null

export const useNotificationsStore = defineStore('notifications', {
  state: () => ({
    items: [] as NotificationRow[],
    unreadCount: 0,
    loading: false,
    loaded: false,
  }),

  actions: {
    async fetch() {
      this.loading = true
      try {
        const response = await axiosInstance.get<NotificationRow[]>('/api/notifications/')

        this.items = response.data
        this.unreadCount = response.data.filter(n => !n.is_read).length
        this.loaded = true
      }
      catch {
        // Keep the previous state on failure — a transient error shouldn't
        // wipe the list the user is looking at.
      }
      finally {
        this.loading = false
      }
    },

    async fetchUnreadCount() {
      try {
        const response = await axiosInstance.get<{ count: number }>(
          '/api/notifications/unread-count/',
        )

        this.unreadCount = response.data.count
      }
      catch {
        // ignore — the badge just won't update this tick.
      }
    },

    async markRead(id: number) {
      const item = this.items.find(n => n.id === id)
      if (!item || item.is_read)
        return

      // Optimistic update — revert if the request fails.
      item.is_read = true
      this.unreadCount = Math.max(0, this.unreadCount - 1)
      try {
        await axiosInstance.post(`/api/notifications/${id}/mark-read/`)
      }
      catch {
        item.is_read = false
        this.unreadCount += 1
      }
    },

    async clearAll() {
      const previousItems = this.items
      const previousUnread = this.unreadCount

      this.items = []
      this.unreadCount = 0
      try {
        await axiosInstance.post('/api/notifications/clear-all/')
      }
      catch {
        this.items = previousItems
        this.unreadCount = previousUnread
      }
    },

    startPolling() {
      this.fetchUnreadCount()
      if (pollTimer)
        return

      pollTimer = setInterval(() => {
        this.fetchUnreadCount()
      }, POLL_INTERVAL_MS)
    },

    stopPolling() {
      if (pollTimer) {
        clearInterval(pollTimer)
        pollTimer = null
      }
      this.items = []
      this.unreadCount = 0
      this.loaded = false
    },
  },
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useNotificationsStore, import.meta.hot))
